import { useEffect } from 'react';
import { sentry } from '../index';

/**
 * Markers already emitted for this page load, keyed by `op::name`.
 */
const emittedMarkers = new Set<string>();

/**
 * Matches sentry's `browserTracingIntegration`'s default `finalTimeout`. Past this point the pageload
 * transaction has certainly closed, so a marker would either attach to an unrelated navigation
 * transaction or open a backdated root transaction of its own.
 */
const PAGE_LOAD_BUDGET_MS = 30_000;

export function resetEmittedMarkersForTests() {
  emittedMarkers.clear();
}

/**
 * Marks a point in the initial page load by starting a sentry span with no timing data of its own,
 * so it shows up in the waterfall of the pageload transaction.
 *
 * This is only for the initial page load, not for later route changes: it emits at most once per
 * page load, and markers reached after the page-load window, or with no span to attach to, are
 * dropped rather than recorded with a misleading duration.
 *
 * @param name - should include the component name and what happened, e.g. 'Weave app mounted'
 * @param op - operation name, e.g. 'ui.weaveapp.mounted'. Pick a distinct one per marker so they
 * stay separable in Sentry. See sentry documentation: https://develop.sentry.dev/sdk/telemetry/traces/span-operations/#js-frameworks
 * @param startTime - optional start time in milliseconds
 */
export function usePageLoadSpanMarker(name: string, op: string, startTime?: number) {
  useEffect(() => {
    const key = `${op}::${name}`;
    if (emittedMarkers.has(key)) {
      return;
    }
    // Claim the key even when the marker is dropped below, so that a later remount can't emit a
    // stale measurement in its place.
    emittedMarkers.add(key);

    // don't record markers after the page load budget has passed, or if there's no active span to attach to
    if (performance.now() > PAGE_LOAD_BUDGET_MS || !sentry.instance.getActiveSpan()) {
      return;
    }

    sentry.instance.startSpan({ name, op, startTime }, () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- name and op should be stable and if they do change, the effect will run again which will mess up perf timings
  }, []);
}

type PageLoadSpanMarkerProps = {
  name: string;
  op: string;
  startTime?: number;
};
// componentized version of the above hook
export function PageLoadSpanMarker({ name, op, startTime }: PageLoadSpanMarkerProps) {
  usePageLoadSpanMarker(name, op, startTime);
  return null;
}

// this returns the same timestamp for the duration of the page load
export function getNavigationStartTime() {
  const [navEntry] = performance.getEntriesByType('navigation');
  return Math.round(performance.timeOrigin + navEntry.startTime);
}
